import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { LoggingService } from '../logging/logging.service';
import { PageConfig } from '../config/entities';
import { ConfigService } from '../config/services/config.service';
import { ToastService } from '../common/services/toast-service/toast-service.service';
import { IonicColor } from '../common/entities/toast/ionic-color';
import { CurafidaFeatureEnum } from '../config/entities/base-page-config';
import { CurafidaAuthService } from '../authentication/curafida-auth.service';
import { UserRoles } from '../user/entities/user-roles';
import { TranslateService } from '@ngx-translate/core';
import { FeatureConfigs } from '../common/entities/curafida-frontend-configuration';
import { ConfigurationDetailFeatures } from '../config/resources/configuration.resource';

/**
 * At the moment pageConfigs that include a URL with path variables as an infix cannot be matched:
 * @example /member/patient/:someID/details
 * This example will only match /member/patient just like /member/patient/:someID and /member/patient will.
 * @param route - ActivatedRouteSnapshot
 * @param state - RouterStateSnapshot
 */
export const RoleGuard: CanActivateFn = (_route, state) => {
    let pageConfig: PageConfig;
    const log = inject(LoggingService).getLogger(RoleGuard.name);

    const authService = inject(CurafidaAuthService);
    const router = inject(Router);
    const configService = inject(ConfigService);
    const toastService = inject(ToastService);
    const translateService = inject(TranslateService);

    const loggedInUser = authService.loggedInUser();
    if (!loggedInUser) throw new Error('Role guard can not be applied on an unauthenticated route!');
    const userRoles = (loggedInUser.roles as string[]) ?? [];
    const url = state.url;
    let pageRoles: string[] = findPageRoles(url);
    if (pageRoles.length === 0) {
        for (const parentConfig of configService.frontendConfig().pageConfigs) {
            if (parentConfig.childPageConfigs) {
                for (const subPageConfig of parentConfig.childPageConfigs) {
                    if (subPageConfig.url && url.includes(subPageConfig.url)) {
                        pageConfig = subPageConfig;
                        pageRoles = subPageConfig.role;
                    }
                }
            }
        }
    }
    const pageConfigModule = pageConfig?.module;
    if (
        pageConfig?.module &&
        !(
            configService.frontendConfig().features[pageConfigModule as keyof FeatureConfigs]?.enabled ||
            configService.config()?.configuration?.features[pageConfigModule as keyof ConfigurationDetailFeatures]
                ?.enabled
        ) &&
        pageConfig.module !== CurafidaFeatureEnum.common
    ) {
        log.warn(`[canActivate] Module not activate (${pageConfig.module}) for the page ${url}. Navigating to root /`);
        const rootCompatibleWithCurrentUser = configService
            .frontendConfig()
            .rootUrls.filter((p) => userRoles.includes(p.role as UserRoles));
        if (rootCompatibleWithCurrentUser.length === 1 && rootCompatibleWithCurrentUser[0]?.url === url) {
            toastService.showToast('AUTHORIZATION.NO_SITE_ENTITLEMENT_LOGOUT', IonicColor.danger);
            setTimeout(() => {
                authService.logout();
            }, 10000);
            return false;
        } else {
            toastService.showToast('AUTHORIZATION.NO_SITE_ENTITLEMENT', IonicColor.danger);
            return router.parseUrl('/');
        }
    }
    if (pageRoles.length === 0) {
        log.debug(`[canActivate] No specific role required. Stay on ${url} and return true.`);
        return true;
    }
    for (const userRole of userRoles) {
        if (pageRoles.includes(userRole)) {
            log.debug(`[canActivate] User has required roles. Stay on ${url} and return true.`);
            return true;
        }
    }
    log.warn(`[canActivate] Insufficient Roles (${pageRoles}) for the page ${url}. Navigating to root /`);
    toastService.showToast('AUTHORIZATION.NO_SITE_ENTITLEMENT', IonicColor.danger);
    return router.parseUrl('/');

    function trimUrl(url: string): string {
        if (!url || url.length <= 1) {
            return '';
        }
        if (url.charAt(0) === '/') {
            url = url.substring(1);
        }
        if (url.charAt(url.length - 1) === '/') {
            url = url.substring(0, url.length - 1);
        }
        return url;
    }

    function findPageRoles(url: string, pageRoles: string[] = []): string[] {
        if ((pageRoles && pageRoles.length > 0) || url.length <= 1) {
            return pageRoles;
        }
        url = trimUrl(url);
        const matchingPageConfig = configService
            .frontendConfig()
            .pageConfigs.find((pageConfig) => trimUrl(pageConfig.url) === url);
        pageConfig = matchingPageConfig;
        pageRoles = matchingPageConfig ? matchingPageConfig.role : [];
        return findPageRoles(url.substring(0, url.lastIndexOf('/')), pageRoles);
    }
};
